import React from 'react';

import { t as translate } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as EmptyData } from '../../../../../assets/icons/empty_box.svg';
import CustomEmptyPage from '../../../../../shared/components/CustomEmptyPage/CustomEmptyPage';
import StyledTable from '../../../../../shared/components/StyledTable/StyledTable';
import FormattedDate from '../../../../../shared/components/v3/Table/StyledColumns/Dates/FormattedDate/FormattedDate';
import TablePaginationV3 from '../../../../../shared/components/v3/TablePagination/TablePagination';
import { NEW_STORY_ROUTE } from '../../../../../shared/constants/auth';
import { DEFAULT_PAGINATOR_OPTIONS_V3 } from '../../../../../shared/constants/settings';
import { ORDER_ASC, ORDER_DESC } from '../../../../../shared/constants/stories';
import { CLICK_ACTION, NEW_STORY, SECTION_STORIES } from '../../../../../shared/constants/tagManager';
import { IStoryListData } from '../../../../../shared/interfaces/stories';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_STORIES_LIST } from '../../../../../shared/translations/stories';
import { GenericFunction } from '../../../../../shared/types/genericTypes';
import { StoryTypeEnum } from '../../../../../shared/types/stories';
import StoryListActions from '../StoryListActions/StoryListActions';
import './StoriesListTable.scss';

export const STORY_LIST_COLUMNS = [
  {
    name: TRANSLATION_STORIES_LIST.table.header.title,
    key: 'title',
    class: ''
  },
  {
    name: TRANSLATION_STORIES_LIST.table.header.author,
    key: 'author',
    class: 'table-hide-mobile'
  },
  {
    name: TRANSLATION_STORIES_LIST.table.header.type,
    key: 'mediaType',
    class: 'table-hide-mobile',
    render: (story: IStoryListData) => {
      return story.mediaType === StoryTypeEnum.image
        ? translate(TRANSLATION_STORIES_LIST.table.type.static)
        : translate(TRANSLATION_STORIES_LIST.table.type.video);
    }
  },
  {
    name: TRANSLATION_STORIES_LIST.table.header.dateFrom,
    key: 'startDate',
    class: 'table-hide-tablet',
    isSortable: true,
    render: (story: IStoryListData) => {
      return <FormattedDate dateString={story.startDate} />;
    }
  },
  {
    name: TRANSLATION_STORIES_LIST.table.header.dateEnd,
    key: 'endDate',
    class: 'table-hide-tablet',
    render: (story: IStoryListData) => {
      return story.endDate ? <FormattedDate dateString={story.endDate} /> : '-';
    }
  },
  {
    name: TRANSLATION_STORIES_LIST.table.header.state,
    key: 'state',
    class: 'table-hide-tablet',
    render: (story: IStoryListData) => {
      return (
        <div className={`story-list-table-state state-${story.status}`}>
          {translate(TRANSLATION_STORIES_LIST.table.state[story.status])}
        </div>
      );
    }
  },
  {
    name: TRANSLATION_STORIES_LIST.table.header.actions,
    key: 'actions',
    class: 'table-hide-tablet table-align-right',
    render: (story: IStoryListData) => {
      return <StoryListActions storyData={story} />;
    }
  }
];

interface IStoriesListTableProps {
  storiesListData: IStoryListData[] | undefined;
  loading: boolean;
  error: boolean;
  totalStories: number;
  paginatorData: {
    paginatorState: number;
    changePaginatorState: GenericFunction;
    actualPage: number;
    changeActualPage: GenericFunction;
  };
  order: {
    orderBy: string;
    changeOrder: GenericFunction;
  };
}

const StoriesListTable = ({
  storiesListData,
  loading,
  error,
  totalStories,
  paginatorData,
  order
}: IStoriesListTableProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const sortData = () => {
    order.orderBy === ORDER_DESC ? order.changeOrder(ORDER_ASC) : order.changeOrder(ORDER_DESC);
  };

  const reloadPage = () => {
    window.location.reload();
  };

  const goToNewStory = () => {
    navigate(NEW_STORY_ROUTE);
    TagManagerService.pushEvent(CLICK_ACTION, NEW_STORY);
  };

  if (error) {
    return (
      <div>
        <CustomEmptyPage
          title={t(TRANSLATION_STORIES_LIST.error.title)}
          subtitle={t(TRANSLATION_STORIES_LIST.error.subtitle)}
          newExperience={true}
          buttonText={t(TRANSLATION_STORIES_LIST.error.buttonText)}
          buttonAction={reloadPage}
        />
      </div>
    );
  }

  if (!loading && !storiesListData) {
    return (
      <div>
        <CustomEmptyPage
          title={t(TRANSLATION_STORIES_LIST.noStories.title)}
          subtitle={t(TRANSLATION_STORIES_LIST.noStories.subtitle)}
          newExperience={true}
          CustomImage={EmptyData}
          buttonText={t(TRANSLATION_STORIES_LIST.noStories.buttonText)}
          buttonAction={goToNewStory}
        />
      </div>
    );
  }

  return (
    <div className="story-list-table">
      <StyledTable columns={STORY_LIST_COLUMNS} data={storiesListData} error={error} sortData={sortData} />
      <TablePaginationV3
        rowsPerPageOptions={DEFAULT_PAGINATOR_OPTIONS_V3}
        rowsPerPage={paginatorData.paginatorState}
        count={totalStories}
        actualPage={paginatorData.actualPage}
        marginTop={24}
        onPageChange={paginatorData.changeActualPage}
        onRowsPerPageChange={paginatorData.changePaginatorState}
        sectionPage={SECTION_STORIES}
      />
    </div>
  );
};

export default StoriesListTable;
